// A COARSE LOCATION WITHOUT ASKING FOR ONE (Randy, 2026-09-12).
//
// When server IP inference is unavailable, the home page can still use two
// browser signals without a geolocation permission prompt:
//
//   1. THE TIME ZONE (`Intl.DateTimeFormat().resolvedOptions().timeZone`),
//      mapped to a coarse coast. `America/New_York` is the US east coast.
//   2. THE LOCALE, mapped to a country, when the zone says nothing.
//
// Precise GPS still upgrades over both when it is granted.
//
// WHAT THESE ARE NOT. They are not a position, they are a REGION, and the point
// each row carries is a representative harbour for that region rather than
// where anyone is. A reader in Buffalo gets New York Harbor from
// `America/New_York`; that is the intended accuracy, and it is why the caller
// must not print a distance from a timezone/locale fix (see home/index.jsx).
//
// UNKNOWN FALLS THROUGH TO NOTHING, deliberately. A zone this table does not
// know returns null, the locale is tried, and if that is unknown too the page
// has no location — which is what makes the By Country list appear. Guessing
// badly is worse than not guessing: the whole value of a coarse fix is that the
// first screen is about the right ocean.
//
// THE POINTS ARE COASTAL, not city centres. A tide station lookup from Paris or
// Chicago's downtown is a lookup from inland, so each row names the harbour the
// region reads from: Le Havre for France, Southampton for the UK. The Great
// Lakes entries are the exception that proves the rule — Chicago and Toronto
// ARE on the water, and the lakes carry stations.
//
// The home page first asks V2 for trusted CloudFront IP inference. These tables
// remain the fallback when that result is absent. IP-derived distances are an
// explicit product choice; these representative timezone/locale points still
// do not display distances. See home/_useNearbyLocations.js.

// Zone -> region. One line per zone, and the label is what a caller would show
// if it ever needed to say where it thinks the reader is.
const ZONES = {
  // North America, Atlantic
  'America/New_York': { lat: 40.7, lng: -74.01, label: 'US East Coast' },
  'America/Halifax': { lat: 44.65, lng: -63.57, label: 'Nova Scotia' },
  'America/Moncton': { lat: 45.27, lng: -66.06, label: 'New Brunswick' },
  'America/St_Johns': { lat: 47.57, lng: -52.71, label: 'Newfoundland' },
  // North America, Gulf and the lakes
  'America/Chicago': { lat: 41.89, lng: -87.61, label: 'Lake Michigan' },
  'America/Detroit': { lat: 42.33, lng: -83.05, label: 'Great Lakes' },
  'America/Toronto': { lat: 43.64, lng: -79.38, label: 'Lake Ontario' },
  // North America, Pacific
  'America/Los_Angeles': { lat: 33.72, lng: -118.27, label: 'US West Coast' },
  'America/Vancouver': { lat: 49.29, lng: -123.12, label: 'British Columbia' },
  'America/Anchorage': { lat: 61.24, lng: -149.89, label: 'Alaska' },
  'America/Juneau': { lat: 58.3, lng: -134.42, label: 'Southeast Alaska' },
  'Pacific/Honolulu': { lat: 21.31, lng: -157.87, label: 'Hawaii' },
  // South America
  'America/Sao_Paulo': { lat: -23.97, lng: -46.33, label: 'Brazil' },
  'America/Argentina/Buenos_Aires': { lat: -34.6, lng: -58.37, label: 'Argentina' },
  // Europe
  'Europe/London': { lat: 50.9, lng: -1.4, label: 'United Kingdom' },
  'Europe/Dublin': { lat: 53.35, lng: -6.24, label: 'Ireland' },
  'Europe/Paris': { lat: 49.49, lng: 0.11, label: 'France' },
  'Europe/Brussels': { lat: 51.23, lng: 2.92, label: 'Belgium' },
  'Europe/Amsterdam': { lat: 52.46, lng: 4.55, label: 'Netherlands' },
  'Europe/Berlin': { lat: 53.55, lng: 9.99, label: 'Germany' },
  'Europe/Copenhagen': { lat: 55.68, lng: 12.6, label: 'Denmark' },
  'Europe/Oslo': { lat: 59.9, lng: 10.74, label: 'Norway' },
  'Europe/Stockholm': { lat: 59.33, lng: 18.07, label: 'Sweden' },
  'Europe/Helsinki': { lat: 60.17, lng: 24.94, label: 'Finland' },
  'Europe/Lisbon': { lat: 38.7, lng: -9.42, label: 'Portugal' },
  'Europe/Madrid': { lat: 36.53, lng: -6.29, label: 'Spain' },
  'Europe/Rome': { lat: 42.09, lng: 11.79, label: 'Italy' },
  'Europe/Athens': { lat: 37.94, lng: 23.65, label: 'Greece' },
  // Asia and Oceania
  'Asia/Tokyo': { lat: 35.45, lng: 139.66, label: 'Japan' },
  'Asia/Seoul': { lat: 37.45, lng: 126.6, label: 'South Korea' },
  'Asia/Taipei': { lat: 25.15, lng: 121.74, label: 'Taiwan' },
  'Asia/Hong_Kong': { lat: 22.29, lng: 114.17, label: 'Hong Kong' },
  'Asia/Shanghai': { lat: 31.23, lng: 121.47, label: 'China' },
  'Asia/Singapore': { lat: 1.26, lng: 103.82, label: 'Singapore' },
  'Australia/Sydney': { lat: -33.86, lng: 151.21, label: 'New South Wales' },
  'Australia/Melbourne': { lat: -37.84, lng: 144.92, label: 'Victoria' },
  'Australia/Brisbane': { lat: -27.37, lng: 153.16, label: 'Queensland' },
  'Australia/Adelaide': { lat: -34.78, lng: 138.5, label: 'South Australia' },
  'Australia/Perth': { lat: -32.05, lng: 115.74, label: 'Western Australia' },
  'Pacific/Auckland': { lat: -36.84, lng: 174.77, label: 'New Zealand' },
}

// Locale -> country, for when the zone is unknown. Keyed on the FULL tag first
// and then the language, so `en-GB` finds the UK and `fr-CA` finds Nova Scotia
// rather than France.
//
// BARE `en` IS DELIBERATELY ABSENT, and it is the most important row in the
// table for not existing. English is this app's default locale and the fallback
// for every unmatched language, so mapping it to the US would hand a US harbour
// to a reader in Kenya, in India, or anywhere the zone table has a gap — which
// is precisely the population this fallback is for.
const LOCALES = {
  'en-GB': { lat: 50.9, lng: -1.4, label: 'United Kingdom' },
  'en-IE': { lat: 53.35, lng: -6.24, label: 'Ireland' },
  'en-AU': { lat: -33.86, lng: 151.21, label: 'Australia' },
  'en-NZ': { lat: -36.84, lng: 174.77, label: 'New Zealand' },
  'en-CA': { lat: 44.65, lng: -63.57, label: 'Canada' },
  'en-US': { lat: 40.7, lng: -74.01, label: 'United States' },
  'fr-CA': { lat: 44.65, lng: -63.57, label: 'Canada' },
  'pt-BR': { lat: -23.97, lng: -46.33, label: 'Brazil' },
  'zh-TW': { lat: 25.15, lng: 121.74, label: 'Taiwan' },
  'zh-HK': { lat: 22.29, lng: 114.17, label: 'Hong Kong' },
  fr: { lat: 49.49, lng: 0.11, label: 'France' },
  de: { lat: 53.55, lng: 9.99, label: 'Germany' },
  nl: { lat: 52.46, lng: 4.55, label: 'Netherlands' },
  da: { lat: 55.68, lng: 12.6, label: 'Denmark' },
  nb: { lat: 59.9, lng: 10.74, label: 'Norway' },
  no: { lat: 59.9, lng: 10.74, label: 'Norway' },
  sv: { lat: 59.33, lng: 18.07, label: 'Sweden' },
  fi: { lat: 60.17, lng: 24.94, label: 'Finland' },
  es: { lat: 36.53, lng: -6.29, label: 'Spain' },
  pt: { lat: 38.7, lng: -9.42, label: 'Portugal' },
  it: { lat: 42.09, lng: 11.79, label: 'Italy' },
  el: { lat: 37.94, lng: 23.65, label: 'Greece' },
  ja: { lat: 35.45, lng: 139.66, label: 'Japan' },
  ko: { lat: 37.45, lng: 126.6, label: 'South Korea' },
  zh: { lat: 31.23, lng: 121.47, label: 'China' },
}

export function regionForZone(zone) {
  return (typeof zone === 'string' && ZONES[zone]) || null
}

// Full tag, then language. Case is normalised on the language only: BCP 47
// writes the region in caps (`en-GB`), and a browser that hands back `en-gb`
// should still find it.
export function regionForLocale(locale) {
  if (typeof locale !== 'string' || locale === '') return null
  const [lang, region] = locale.split(/[-_]/)
  if (!lang) return null
  const tag = region ? `${lang.toLowerCase()}-${region.toUpperCase()}` : null
  return (tag && LOCALES[tag]) || LOCALES[lang.toLowerCase()] || null
}

// The browser's own answers, read defensively: `Intl` is everywhere this app
// runs, but a locked-down environment can make `resolvedOptions` throw, and a
// thrown getter here would take the home page down for a nice-to-have.
export function browserSignals() {
  let timeZone = null
  let locale = null
  try {
    const resolved = Intl?.DateTimeFormat?.().resolvedOptions?.()
    timeZone = resolved?.timeZone ?? null
    locale = resolved?.locale ?? null
  } catch {
    /* no Intl: both signals stay null and the page has no coarse location */
  }
  if (!locale && typeof navigator !== 'undefined') locale = navigator.language ?? null
  return { timeZone, locale }
}

// The whole rule, in the order the brief sets: zone, then locale, then nothing.
//
// `source` is what the caller needs, not decoration: a coarse fix must not
// print a distance, and 'gps' is the only source that may.
export function coarseLocation({ timeZone, locale } = browserSignals()) {
  const byZone = regionForZone(timeZone)
  if (byZone) return { ...byZone, source: 'timezone', precise: false }
  const byLocale = regionForLocale(locale)
  if (byLocale) return { ...byLocale, source: 'locale', precise: false }
  return null
}

// Exported for the tests, so a table edit that breaks a coastal point or
// duplicates a zone fails rather than being reviewed by eye.
export const COARSE_ZONES = ZONES
export const COARSE_LOCALES = LOCALES
